/**
 * 导演项目侧栏（参考电商复刻 TaskSidebar）
 *
 * 顶部黑色块包含「新建项目」按钮，下方为项目卡片列表。
 * 卡片：缩略图（首个分镜的锁定 keyframe）+ 项目名 + 进度（X/Y 已激活）+ 状态徽标。
 * 窄屏作为左侧抽屉使用。
 */

import {
  Clapperboard,
  ImageIcon,
  Loader2,
  Plus,
  Trash2,
  Archive,
  ArchiveRestore,
  Pencil,
  Share2,
} from 'lucide-react';
import { formatInBeijing } from '../../../utils/time';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';
import type {
  DirectorProjectStatus,
  DirectorProjectWithCover,
} from '../services/directorApi';

interface Props {
  projects: DirectorProjectWithCover[];
  currentProjectId: string | null;
  loading?: boolean;
  filter: 'active' | 'archived';
  onChangeFilter: (filter: 'active' | 'archived') => void;
  onSelectProject: (id: string) => void;
  onCreateProject: () => void;
  onEdit?: (id: string) => void;
  onShare?: (id: string) => void;
  onArchive?: (id: string) => void;
  onUnarchive?: (id: string) => void;
  onDelete?: (id: string) => void;
  className?: string;
}

const STATUS_LABEL: Record<DirectorProjectStatus, { label: string; color: string }> = {
  draft: { label: '草稿', color: 'text-slate-500 bg-slate-100' },
  producing: { label: '制作中', color: 'text-sky-700 bg-sky-100' },
  ready: { label: '待合成', color: 'text-violet-700 bg-violet-100' },
  composing: { label: '合成中', color: 'text-amber-700 bg-amber-100' },
  completed: { label: '已完成', color: 'text-emerald-700 bg-emerald-100' },
  archived: { label: '已归档', color: 'text-slate-500 bg-slate-100' },
};

export function DirectorSidebar({
  projects,
  currentProjectId,
  loading,
  filter,
  onChangeFilter,
  onSelectProject,
  onCreateProject,
  onEdit,
  onShare,
  onArchive,
  onUnarchive,
  onDelete,
  className = '',
}: Props) {
  return (
    <aside
      className={`h-full overflow-y-auto border-white/70 bg-white/75 shadow-[12px_0_40px_rgba(15,23,42,0.04)] backdrop-blur-xl lg:sticky lg:top-16 lg:h-[calc(100vh-64px)] lg:w-80 lg:shrink-0 lg:border-r ${className}`}
    >
      <div className="p-5">
        {/* 顶部黑色块 */}
        <div className="mb-5 rounded-[28px] bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-5 text-white shadow-2xl shadow-slate-300/40">
          <div className="mb-1 flex items-center gap-2">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white/10">
              <Clapperboard className="h-4 w-4 text-sky-200" />
            </span>
            <h2 className="text-lg font-semibold leading-none">项目库</h2>
          </div>
          <p className="mt-2 text-xs text-slate-300">
            用图片打草稿、用视频出成片、自动剪辑成短剧
          </p>
          <button
            type="button"
            onClick={onCreateProject}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-slate-950 transition hover:scale-[1.01] hover:bg-sky-50"
          >
            <Plus className="h-4 w-4" />
            新建导演项目
          </button>
        </div>

        {/* 进行中 / 已归档 切换 */}
        <div className="mb-3 grid grid-cols-2 gap-1.5 rounded-2xl bg-white/80 p-1 shadow-sm">
          <button
            type="button"
            onClick={() => onChangeFilter('active')}
            className={`rounded-xl px-3 py-1.5 text-xs font-semibold transition ${
              filter === 'active'
                ? 'bg-slate-950 text-white shadow-md'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            进行中
          </button>
          <button
            type="button"
            onClick={() => onChangeFilter('archived')}
            className={`rounded-xl px-3 py-1.5 text-xs font-semibold transition ${
              filter === 'archived'
                ? 'bg-slate-950 text-white shadow-md'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            已归档
          </button>
        </div>

        {/* 列表 */}
        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : projects.length === 0 ? (
          <div className="rounded-[24px] border border-dashed border-slate-200 bg-white/70 p-6 text-center">
            <Clapperboard className="mx-auto mb-2 h-10 w-10 text-slate-300" />
            <p className="text-sm font-medium text-slate-700">
              {filter === 'active' ? '暂无项目' : '没有归档项目'}
            </p>
            <p className="mt-1 text-xs text-slate-400">
              {filter === 'active' ? '点上方"新建导演项目"开始' : ''}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {projects.map((project) => {
              const isActive = currentProjectId === project.id;
              const statusInfo = STATUS_LABEL[project.status] ?? STATUS_LABEL.draft;

              return (
                <div
                  key={project.id}
                  onClick={() => onSelectProject(project.id)}
                  className={`group cursor-pointer rounded-3xl border p-3 transition ${
                    isActive
                      ? 'border-sky-200 bg-sky-50/90 shadow-lg shadow-sky-100/70'
                      : 'border-white/80 bg-white/80 hover:border-slate-200 hover:bg-white hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* 缩略图 */}
                    <div className="h-14 w-14 shrink-0 overflow-hidden rounded-2xl bg-slate-100 ring-1 ring-slate-100">
                      {project.coverUrl ? (
                        <img
                          src={resolveBrowserMediaUrl(project.coverUrl)}
                          alt={project.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-950 to-slate-700">
                          <ImageIcon className="h-5 w-5 text-sky-200/70" />
                        </div>
                      )}
                    </div>
                    {/* 内容 */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-sm font-semibold text-slate-900">
                          {project.name}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                        <span className="text-[11px] text-slate-500">
                          {project.shotCount} 个分镜
                        </span>
                        <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-600">
                          {project.aspectRatio}
                        </span>
                        <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-600">
                          {project.resolution}
                        </span>
                        {project.videoModelName && (
                          <span
                            className="max-w-[110px] truncate rounded-full bg-sky-50 px-1.5 py-0.5 text-[9px] font-medium text-sky-700"
                            title={project.videoModelName}
                          >
                            {project.videoModelName}
                          </span>
                        )}
                      </div>
                      <div className="mt-1.5 truncate text-[11px] text-slate-400">
                        {formatInBeijing(project.updatedAt, {
                          month: '2-digit',
                          day: '2-digit',
                        })}{' '}
                        更新
                      </div>
                    </div>
                    {/* 悬浮操作 */}
                    <div
                      className="flex flex-col gap-1 opacity-0 transition group-hover:opacity-100"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {onEdit && (
                        <button
                          type="button"
                          onClick={() => onEdit(project.id)}
                          className="rounded-xl p-1.5 text-slate-400 transition hover:bg-sky-50 hover:text-sky-600"
                          title="编辑项目"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {onShare && project.lastCompositionId && (
                        <button
                          type="button"
                          onClick={() => onShare(project.id)}
                          className="rounded-xl p-1.5 text-slate-400 transition hover:bg-sky-50 hover:text-sky-600"
                          title="分享成片"
                        >
                          <Share2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {project.isArchived
                        ? onUnarchive && (
                            <button
                              type="button"
                              onClick={() => onUnarchive(project.id)}
                              className="rounded-xl p-1.5 text-slate-400 transition hover:bg-sky-50 hover:text-sky-600"
                              title="恢复"
                            >
                              <ArchiveRestore className="h-3.5 w-3.5" />
                            </button>
                          )
                        : onArchive && (
                            <button
                              type="button"
                              onClick={() => onArchive(project.id)}
                              className="rounded-xl p-1.5 text-slate-400 transition hover:bg-amber-50 hover:text-amber-600"
                              title="归档"
                            >
                              <Archive className="h-3.5 w-3.5" />
                            </button>
                          )}
                      {onDelete && (
                        <button
                          type="button"
                          onClick={() => onDelete(project.id)}
                          className="rounded-xl p-1.5 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
                          title="彻底删除"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </aside>
  );
}
